import React, { useMemo, useState, useEffect } from "react";
import { Card } from "@shared/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getLoginDataFromStorage } from "@shared/utils/loginUtils";
import { logEvent } from "@shared/analytics/analytics";
import { useService, useUsageChart } from "../../../dashboard/hook";
import { useTranslation } from 'react-i18next';

export const ConsumptionChart = () => {
  const { remoteUtilityId, remoteConsumerNumber, consumerId } = getLoginDataFromStorage();
  const { t } = useTranslation();

  const { data: serviceData } = useService({
    consumer: consumerId,
  });

  const activeServices = useMemo(
    () => serviceData?.result || [],
    [serviceData]
  );

  const [utilityFilter, setUtilityFilter] = useState("");

  useEffect(() => {
    logEvent("Utility filter opened");
    if (activeServices.length > 0 && !utilityFilter) {
      setUtilityFilter(activeServices[0]);
    }
  }, [activeServices, utilityFilter]);

  const { data: graphData } = useUsageChart({
    consumer_no: remoteConsumerNumber,
    remote_utility_id: remoteUtilityId,
    fetch_last_six_records: "True",
    utility_service: utilityFilter
  });

  const transformedUsageData = useMemo(() => {
    if (!graphData?.result) return [];
    
    return Object.entries(graphData.result).map(([month, data]: [string, any]) => ({
      month: month,
      usage: data.consumption,
      avg: 11, // using a static average for demonstration if backend lacks it
    }));
  }, [graphData]);

  const handleUtilityChange = (value: string) => {
    setUtilityFilter(value);
  };

  return (
    <Card className="p-6 mt-4 shadow-sm border-slate-100 rounded-xl bg-white">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-bold text-slate-900 text-lg">
            Monthly Consumption vs. Household Average
          </h3>
          <p className="text-sm text-slate-500 mt-1">CCF (1 CCF = 748 gallons)</p>
        </div>
        <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
          <Select value={utilityFilter} onValueChange={handleUtilityChange}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Select utility" />
            </SelectTrigger>
            <SelectContent>
              {activeServices.map((service: string, index: number) => (
                <SelectItem key={index} value={service}>
                  {service}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="h-[300px] w-full">
        {transformedUsageData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={transformedUsageData}
              margin={{ top: 10, right: 30, left: -20, bottom: 0 }}
            >
            <defs>
              <linearGradient id="colorUsage" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis
              dataKey="month"
              axisLine={true}
              tickLine={true}
              tick={{ fontSize: 12, fill: "#64748b" }}
            />
            <YAxis
              axisLine={true}
              tickLine={true}
              tick={{ fontSize: 12, fill: "#64748b" }}
              ticks={[0, 4, 8, 12, 16]}
              domain={[0, 16]}
            />
            <Tooltip
              contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
            />
            <Legend
              verticalAlign="bottom"
              height={36}
              iconType="circle"
              content={(props) => {
                const { payload } = props;
                return (
                  <ul className="flex justify-center gap-6 mt-4">
                    {payload?.map((entry, index) => (
                      <li key={`item-${index}`} className="flex items-center text-xs font-medium text-blue-500">
                        {entry.value === t("Your Usage") ? (
                          <div className="flex items-center gap-1">
                            <svg width="24" height="12">
                              <circle cx="12" cy="6" r="3" fill="#3b82f6" />
                              <line x1="0" y1="6" x2="24" y2="6" stroke="#3b82f6" strokeWidth="2" />
                            </svg>
                            <span className="text-[#3b82f6]">{entry.value}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <svg width="24" height="12">
                              <circle cx="12" cy="6" r="3" fill="#fff" stroke="#f97316" strokeWidth="1.5" />
                              <line x1="0" y1="6" x2="24" y2="6" stroke="#f97316" strokeWidth="1.5" strokeDasharray="3 3" />
                            </svg>
                            <span className="text-[#f97316]">{entry.value}</span>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                );
              }}
            />
            <Area
              type="monotone"
              dataKey="avg"
              name={t("Avg Household")}
              stroke="#f97316"
              strokeWidth={1.5}
              strokeDasharray="5 5"
              fill="none"
              dot={false}
              activeDot={false}
            />
            <Area
              type="monotone"
              dataKey="usage"
              name={t("Your Usage")}
              stroke="#3b82f6"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorUsage)"
              dot={{ r: 4, fill: "#fff", stroke: "#3b82f6", strokeWidth: 2 }}
              activeDot={{ r: 6, fill: "#3b82f6", stroke: "#fff", strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            {utilityFilter ? "Loading usage data..." : "Select a utility service to view data"}
          </div>
        )}
      </div>
    </Card>
  );
};
